import { cloneElement, isValidElement, useEffect, useRef, useState } from 'react';
import { Box, FormHelperText, Popover, Stack } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { passwordRules } from '../../URLConstants';

const HOVER_CLOSE_DELAY_MS = 150;

function RulesContent({ password, showErrorState }) {
  return (
    <Stack spacing={1} sx={{ p: 1.5, minWidth: 280 }}>
      {passwordRules.map((rule, index) => {
        const passed = rule.test(password);
        const isFailed = showErrorState && !passed;

        let iconColor = 'text.disabled';
        let textColor = '#9e9e9e';

        if (passed) {
          iconColor = 'success.main';
          textColor = '#54D62C';
        } else if (isFailed) {
          iconColor = '#FF0000';
          textColor = '#FF0000';
        }

        return (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            {passed ? (
              <CheckCircleOutlineOutlinedIcon sx={{ color: iconColor, fontSize: 18 }} />
            ) : (
              <CancelOutlinedIcon sx={{ color: iconColor, fontSize: 18 }} />
            )}
            <span style={{ color: textColor, fontSize: '14px' }}>{rule.label}</span>
          </Stack>
        );
      })}
    </Stack>
  );
}

export default function PasswordRulesChecklist({
  password = '',
  showErrorState = false,
  error = false,
  helperText = '',
  children
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const closeTimerRef = useRef(null);
  const allPassed = passwordRules.every((rule) => rule.test(password));
  const open = Boolean(anchorEl);
  const showHelperRow = !allPassed && Boolean(helperText);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPopover = (event) => {
    clearCloseTimer();
    setAnchorEl(event.currentTarget);
  };

  const scheduleClosePopover = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setAnchorEl(null), HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    if (allPassed) {
      clearCloseTimer();
      setAnchorEl(null);
    }
  }, [allPassed]);

  useEffect(() => () => clearCloseTimer(), []);

  const field = isValidElement(children)
    ? cloneElement(children, {
        error,
        helperText: undefined
      })
    : children;

  if (allPassed) {
    return field;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {field}
      {showHelperRow && (
        <Box
          data-testid="password-rules-helper-row"
          onMouseEnter={openPopover}
          onMouseLeave={scheduleClosePopover}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.375,
            mx: 1.75,
            cursor: 'default'
          }}
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: 16,
              color: 'error.main',
              flexShrink: 0
            }}
            aria-hidden
          />
          <FormHelperText error sx={{ m: 0, p: 0, lineHeight: 1.66, flex: 1 }}>
            {helperText}
          </FormHelperText>
        </Box>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            onMouseEnter: clearCloseTimer,
            onMouseLeave: scheduleClosePopover,
            sx: {
              pointerEvents: 'auto',
              bgcolor: '#fff',
              boxShadow: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mt: -0.5
            }
          }
        }}
      >
        <RulesContent password={password} showErrorState={showErrorState} />
      </Popover>
    </Box>
  );
}
